import {
	CATEGORY_FILTER_OPTIONS,
	EXPAND_FILTER_OPTIONS,
	AUDIT_TRAIL_FILTER_STORAGE_KEY,
	EMPTY_APPLIED_FILTERS,
	SOURCE_FILTER_OPTIONS
} from './constants/constants';
import { parseAuditTrailChildRow } from './auditTrailUtils';

export const flattenCategoryGroups = (categoryGroups = []) => {
	if (!categoryGroups?.length) {
		return [];
	}

	return categoryGroups.flatMap((group) => {
		const option = CATEGORY_FILTER_OPTIONS.find((item) => item.value === group);
		return option?.categories ?? [];
	});
};

export const flattenCollapseGroups = (collapseGroups = []) => {
	if (!collapseGroups?.length) {
		return [];
	}

	return collapseGroups.flatMap((group) => {
		const option = EXPAND_FILTER_OPTIONS.find((item) => item.value === group);
		return option?.categories ?? [];
	});
};

/** Collapse categories for API — all groupable types except those the user chose to expand. */
export const resolveCollapseCategoriesFromExpandGroups = (expandGroups = []) => {
	const expandSet = new Set(expandGroups ?? []);
	const collapseGroups = EXPAND_FILTER_OPTIONS
		.map((option) => option.value)
		.filter((value) => !expandSet.has(value));

	return flattenCollapseGroups(collapseGroups);
};

export const buildAuditTrailRequestPayload = ({ quoteReadableId }) => ({
	QuoteReadableId: quoteReadableId,
	criteria: { OrderBy: 'modify_date DESC' },
	// Always request the full row set; low-priority visibility is applied client-side.
	includeSystem: true,
	signalKey: 'quote-audit-trail'
});

export const normalizeGroupedEvents = (groupedEvents) => {
	if (!groupedEvents) {
		return [];
	}

	const list = Array.isArray(groupedEvents) ? groupedEvents : Object.values(groupedEvents);

	return list.map((child) => parseAuditTrailChildRow(child)).filter(Boolean);
};

export const parseAuditTrailRows = (response) => {
	if (!response || response.error) {
		return { rows: [], resultCount: 0 };
	}

	const results = response.results ?? [];
	const rows = results.map((item) => {
		const row = typeof item === 'string' ? safeParseJson(item) : item;

		if (row?.GroupedEvents) {
			row.GroupedEvents = normalizeGroupedEvents(row.GroupedEvents);
		}

		return row ?? {};
	});

	return {
		rows,
		resultCount: response.resultCount ?? rows.length
	};
};

const safeParseJson = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return {};
	}
};

export const getCategoryGroupLabel = (value) => {
	const option = CATEGORY_FILTER_OPTIONS.find((item) => item.value === value);
	return option?.label ?? value;
};

export const getExpandGroupLabel = (value) => {
	const option = EXPAND_FILTER_OPTIONS.find((item) => item.value === value);
	return option?.label ?? value;
};

export const getSourceFilterLabel = (value) => {
	if (!value) {
		return 'All Sources';
	}

	const option = SOURCE_FILTER_OPTIONS.find((item) => item.value === value);
	return option?.label ?? value;
};

const normalizeIncludeSystem = (value) => {
	if (value === true || value === 1) {
		return true;
	}

	if (value === false || value === 0 || value == null) {
		return false;
	}

	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return normalized === 'true' || normalized === '1';
	}

	return false;
};

export const cloneAppliedFilters = (filters = EMPTY_APPLIED_FILTERS) => ({
	categoryGroups: [...(filters.categoryGroups ?? [])],
	sources: [...(filters.sources ?? [])],
	expandGroups: [...(filters.expandGroups ?? [])],
	includeSystem: normalizeIncludeSystem(filters.includeSystem)
});

const stringArraysEqual = (left = [], right = []) => {
	if (left.length !== right.length) {
		return false;
	}

	for (let index = 0; index < left.length; index += 1) {
		if (left[index] !== right[index]) {
			return false;
		}
	}

	return true;
};

/** True when two filter objects have the same applied values. */
export const areAuditTrailFiltersEqual = (left = {}, right = {}) => {
	const normalizedLeft = sanitizeAuditTrailFilters(left);
	const normalizedRight = sanitizeAuditTrailFilters(right);

	return normalizedLeft.includeSystem === normalizedRight.includeSystem
		&& stringArraysEqual(normalizedLeft.categoryGroups, normalizedRight.categoryGroups)
		&& stringArraysEqual(normalizedLeft.sources, normalizedRight.sources)
		&& stringArraysEqual(normalizedLeft.expandGroups, normalizedRight.expandGroups);
};

/** Clears category/source/expand filters; preserves low-priority-events toggle. */
export const buildClearedCategoryFilters = (currentFilters = EMPTY_APPLIED_FILTERS) => ({
	categoryGroups: [],
	sources: [],
	expandGroups: [...EMPTY_APPLIED_FILTERS.expandGroups],
	includeSystem: normalizeIncludeSystem(currentFilters.includeSystem)
});

const VALID_CATEGORY_GROUPS = new Set(CATEGORY_FILTER_OPTIONS.map((option) => option.value));
const VALID_SOURCES = new Set(SOURCE_FILTER_OPTIONS.map((option) => option.value));
const VALID_EXPAND_GROUPS = new Set(EXPAND_FILTER_OPTIONS.map((option) => option.value));
const ALL_EXPAND_GROUP_VALUES = EXPAND_FILTER_OPTIONS.map((option) => option.value);

const sanitizeStringArray = (values, allowedValues) => {
	if (!Array.isArray(values)) {
		return [];
	}

	return values.filter((value) => allowedValues.has(value));
};

/** Maps legacy collapseGroups (selected to collapse) into expandGroups (selected to expand). */
export const migrateLegacyCollapseGroups = (filters = {}) => {
	if (Array.isArray(filters.expandGroups)) {
		return filters;
	}

	if (Array.isArray(filters.collapseGroups)) {
		const collapsed = new Set(filters.collapseGroups);
		return {
			...filters,
			expandGroups: ALL_EXPAND_GROUP_VALUES.filter((value) => !collapsed.has(value))
		};
	}

	return { ...filters, expandGroups: [] };
};

export const sanitizeAuditTrailFilters = (filters = {}) => {
	const migrated = migrateLegacyCollapseGroups(filters);

	return {
		categoryGroups: sanitizeStringArray(migrated.categoryGroups, VALID_CATEGORY_GROUPS),
		sources: sanitizeStringArray(migrated.sources, VALID_SOURCES),
		expandGroups: sanitizeStringArray(migrated.expandGroups, VALID_EXPAND_GROUPS),
		includeSystem: normalizeIncludeSystem(migrated.includeSystem)
	};
};

export const loadSavedAuditTrailFilters = () => {
	if (typeof window === 'undefined' || !window.localStorage) {
		return cloneAppliedFilters(EMPTY_APPLIED_FILTERS);
	}

	try {
		const raw = window.localStorage.getItem(AUDIT_TRAIL_FILTER_STORAGE_KEY);

		if (!raw) {
			return cloneAppliedFilters(EMPTY_APPLIED_FILTERS);
		}

		return cloneAppliedFilters(sanitizeAuditTrailFilters(JSON.parse(raw)));
	} catch {
		return cloneAppliedFilters(EMPTY_APPLIED_FILTERS);
	}
};

export const saveAuditTrailFilters = (filters = EMPTY_APPLIED_FILTERS) => {
	if (typeof window === 'undefined' || !window.localStorage) {
		return;
	}

	try {
		const sanitized = sanitizeAuditTrailFilters(filters);
		window.localStorage.setItem(AUDIT_TRAIL_FILTER_STORAGE_KEY, JSON.stringify(sanitized));
	} catch {
		// Ignore quota / privacy mode errors — filters still work for the session.
	}
};

/** Persists only the low-priority-events toggle; leaves other saved filter fields unchanged. */
export const saveAuditTrailIncludeSystem = (includeSystem) => {
	const saved = loadSavedAuditTrailFilters();
	saveAuditTrailFilters({ ...saved, includeSystem });
};
